import { IcTension } from '@assets/icons/40x40';
import { For } from '@chakra-ui/react';
import DataSheetCard from '@components/common/layout/DataSheetCard';
import { ToastStatusEnum } from '@components/ui/toaster';
import { usePostEquipmentCommandMutation } from '@services/c2/c2.api';
import { createSensorToast } from '@utils/sensors/sensors.utils';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { EquipmentCommandTypeEnum, WebRelayActuatorStateEnum } from '@/types/c2/c2.types';
import { MonitoringEquipment } from '@/types/sensor/monitoring.types';
import { EquipmentConnectionStatusEnum, WebRelayStatus } from '@/types/sensor/sensors';

function toggleActuatorState(state: WebRelayActuatorStateEnum): WebRelayActuatorStateEnum {
  return state === WebRelayActuatorStateEnum.ON ? WebRelayActuatorStateEnum.OFF : WebRelayActuatorStateEnum.ON;
}

type WebRelayButtonProps = {
  monitoringWebRelay: MonitoringEquipment;
  replayMode?: boolean;
};

function WebRelayContainer({ monitoringWebRelay, replayMode }: Readonly<WebRelayButtonProps>) {
  const { formatMessage } = useIntl();
  const [postEquipmentCommand] = usePostEquipmentCommandMutation();

  const isActive = monitoringWebRelay.configuration.active;
  const status = 'actuatorState' in monitoringWebRelay ? monitoringWebRelay : null;
  const isConnected = status?.connectionStatus === EquipmentConnectionStatusEnum.CONNECTED;
  const isDisabled = replayMode || !isActive || !isConnected;

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [prevActuatorState, setPrevActuatorState] = useState<WebRelayActuatorStateEnum | undefined>(
    status?.actuatorState,
  );
  if (prevActuatorState !== status?.actuatorState) {
    setPrevActuatorState(status?.actuatorState);
    setIsLoading(false);
  }

  const actions = [
    {
      action: 'toggle',
      i18nKey: 'sensors.tab.maintenance.webRelay.label',
      values: { actuatorState: status?.actuatorState },
    },
  ];

  function toggleWebRelayState(status: WebRelayStatus) {
    setIsLoading(true);
    const toastParams = {
      commandValues: { state: status.actuatorState },
      command: 'sensors.commands.webRelay.command',
      name: status.configuration.name,
    };
    createSensorToast({
      ...toastParams,
      toast: ToastStatusEnum.INFO,
    });
    postEquipmentCommand({
      site: status.configuration.site,
      appCode: status.configuration.appCode,
      command: {
        type: EquipmentCommandTypeEnum.SET_WR_ACTUATOR_STATE,
        equipmentCode: status.configuration.code,
        actuatorState: toggleActuatorState(status.actuatorState),
      },
    })
      .unwrap()
      .then(() =>
        createSensorToast({
          ...toastParams,
          toast: ToastStatusEnum.SUCCESS,
        }),
      )
      .catch(() => {
        setIsLoading(false);
        createSensorToast({
          ...toastParams,
          toast: ToastStatusEnum.INFO,
        });
      });
  }

  function getTooltipContent(monitoringWebRelay: MonitoringEquipment) {
    const status = 'actuatorState' in monitoringWebRelay ? monitoringWebRelay : null;
    const isConnected = status?.connectionStatus === EquipmentConnectionStatusEnum.CONNECTED;
    if (!monitoringWebRelay.configuration.active) {
      return formatMessage(
        { id: 'sensors.tab.maintenance.webRelay.info.inactive' },
        { name: monitoringWebRelay.configuration.name },
      );
    }
    if (!isConnected) {
      return formatMessage(
        { id: 'sensors.tab.maintenance.webRelay.info.badStatus' },
        { name: monitoringWebRelay.configuration.name },
      );
    }
    return formatMessage(
      { id: 'sensors.tab.maintenance.webRelay.info.state' },
      {
        name: monitoringWebRelay.configuration.name,
        actuatorState: status.actuatorState,
      },
    );
  }

  return (
    <DataSheetCard
      key={monitoringWebRelay.configuration.code}
      icon={IcTension}
      tooltip={getTooltipContent(monitoringWebRelay)}
      actions={actions}
      description={formatMessage(
        { id: 'sensors.tab.maintenance.webRelay.description' },
        { name: monitoringWebRelay.configuration.name },
      )}
      isLoading={isLoading}
      isDisabled={isDisabled}
      onClick={() => !isDisabled && toggleWebRelayState(status)}
    />
  );
}

type WebRelayMaintenanceActionsProps = {
  monitoringWebRelays: MonitoringEquipment[];
  replayMode?: boolean;
};

export default function WebRelayMaintenanceActions({
  monitoringWebRelays,
  replayMode,
}: Readonly<WebRelayMaintenanceActionsProps>) {
  if (!monitoringWebRelays.length) {
    return null;
  }

  return (
    <For each={monitoringWebRelays}>
      {(monitoringWebRelay) => (
        <WebRelayContainer
          key={monitoringWebRelay.configuration.uniqueKey}
          monitoringWebRelay={monitoringWebRelay}
          replayMode={replayMode}
        />
      )}
    </For>
  );
}
